import type { KkmJournalPhase } from './journal';
import type { KkmServerBridge, KkmServerCommand, KkmServerResponse } from './protocol';

import { AMBIGUOUS_OUTCOME_MESSAGE, debugAdapterIssue, KkmServerAdapterError } from './errors';
import {
  KkmProtocolValidationError,
  isKkmCommandStatus,
  isRecord,
  leadingNumericCode,
  parseKkmServerResponse,
  unwrapKkmServerResult,
} from './protocol';

export interface KkmJournalCapture {
  errorCode?: string | null;
  payload: unknown;
  phase: KkmJournalPhase;
  startedAt?: number;
  status?: KkmServerResponse['status'] | null;
  vendorMessage?: string | null;
}

interface KkmBridgeInvocation {
  bridge: KkmServerBridge | undefined;
  capture: (capture: KkmJournalCapture) => void;
  command: KkmServerCommand;
  isCancelled: () => boolean;
  now: () => number;
  signal: AbortSignal;
  timeoutMs: number;
}

/** Один вызов моста: callback, watchdog и cleanup завершают его ровно один раз. */
export async function invokeKkmBridge(input: KkmBridgeInvocation): Promise<KkmServerResponse> {
  const { bridge, command, signal, timeoutMs } = input;

  if (bridge === undefined || typeof bridge.Execute !== 'function') {
    throw new KkmServerAdapterError({
      code: 'bridge_missing',
      command: command.Command,
      operationId: command.IdCommand,
      outcome: 'known_not_run',
      safeMessage:
        'Связь с кассой прервалась до начала операции. Дождитесь автоматической проверки и попробуйте снова.',
    });
  }

  return new Promise<KkmServerResponse>((resolve, reject) => {
    const startedAt = input.now();
    let cleanup: (() => void) | undefined;
    let settled = false;
    const finish = (callback: () => void): void => {
      if (settled) {
        return;
      }

      settled = true;
      clearTimeout(timer);
      signal.removeEventListener('abort', handleAbort);
      cleanup?.();
      callback();
    };
    const handleAbort = (): void => {
      const cancelled = input.isCancelled();
      finish(() => {
        input.capture({
          payload: { cancelled },
          phase: 'timeout',
          startedAt,
        });
        reject(
          new KkmServerAdapterError({
            code: 'operation_timeout',
            command: command.Command,
            operationId: command.IdCommand,
            outcome: cancelled ? 'user_cancelled' : 'unknown',
            safeMessage: cancelled ? 'Операция отменена пользователем.' : AMBIGUOUS_OUTCOME_MESSAGE,
          }),
        );
      });
    };
    const timer = setTimeout(
      () => {
        finish(() => {
          input.capture({
            payload: { cancelled: false },
            phase: 'timeout',
            startedAt,
          });
          reject(
            new KkmServerAdapterError({
              code: 'operation_timeout',
              command: command.Command,
              operationId: command.IdCommand,
              outcome: 'unknown',
              safeMessage: AMBIGUOUS_OUTCOME_MESSAGE,
            }),
          );
        });
      },
      Math.max(1, timeoutMs),
    );

    const registerCleanup = (returnedCleanup: ReturnType<KkmServerBridge['Execute']>): void => {
      if (typeof returnedCleanup === 'function') {
        cleanup = returnedCleanup;

        if (settled) {
          cleanup();
        }
      }
    };
    const handleFailure = (error: unknown): void => {
      if (!settled) {
        input.capture({
          payload: journalThrownPayload(error),
          phase: 'threw',
          startedAt,
          vendorMessage: error instanceof Error ? error.message : String(error),
        });
      }
      debugAdapterIssue('local_component_call_failed', {
        command: command.Command,
        technicalErrorType: error instanceof Error ? error.name : typeof error,
      });
      finish(() => {
        reject(
          new KkmServerAdapterError({
            code: 'component_missing',
            command: command.Command,
            operationId: command.IdCommand,
            outcome: 'known_not_run',
            safeMessage:
              'Программа KKMServer на этом компьютере не отвечает. Проверьте, что она запущена — значок KKMServer в системном трее Windows, — при необходимости запустите её заново.',
          }),
        );
      });
    };

    signal.addEventListener('abort', handleAbort, { once: true });
    input.capture({
      payload: command,
      phase: 'request',
    });

    try {
      const returnedCleanup = bridge.Execute((rawResponse) => {
        const result = unwrapKkmServerResult(rawResponse);
        if (result !== rawResponse) {
          debugAdapterIssue('result_envelope_unwrapped', { command: command.Command });
        }
        const journalFields = journalResponseFields(result);
        input.capture({
          errorCode: journalFields.errorCode,
          payload: rawResponse,
          phase: 'response',
          startedAt,
          status: journalFields.status,
          vendorMessage: journalFields.vendorMessage,
        });

        try {
          const response = parseKkmServerResponse(result, command.IdCommand);
          finish(() => {
            resolve(response);
          });
        } catch (error) {
          debugAdapterIssue('malformed_command_response', {
            command: command.Command,
            technicalMessage:
              error instanceof KkmProtocolValidationError
                ? error.message
                : 'The browser bridge returned a malformed response.',
          });
          finish(() => {
            reject(
              new KkmServerAdapterError({
                code: 'malformed_response',
                command: command.Command,
                operationId: command.IdCommand,
                outcome: 'unknown',
                safeMessage: 'Касса ответила неожиданно. Не повторяйте операцию вручную: Mors проверит результат.',
              }),
            );
          });
        }
      }, command);

      registerCleanup(returnedCleanup);
    } catch (error) {
      handleFailure(error);
    }
  });
}

interface JournalResponseFields {
  errorCode: string | null;
  status: KkmServerResponse['status'] | null;
  vendorMessage: string | null;
}

/**
 * Журнал пишется до валидации протокола, поэтому диагностические поля
 * снимаются напрямую с сырого ответа за один проход. Вызывающий передаёт уже
 * развёрнутый результат: колонки статуса и ошибки описывают исходную команду,
 * а payload журнала сохраняет envelope `GetRezult` целиком.
 */
function journalResponseFields(value: unknown): JournalResponseFields {
  if (!isRecord(value)) {
    return { errorCode: null, status: null, vendorMessage: null };
  }

  const messages = [value.Error, value.Message, value.Warning].filter(
    (item): item is string => typeof item === 'string' && item.trim() !== '',
  );

  return {
    errorCode: leadingNumericCode(messages[0]),
    status: isKkmCommandStatus(value.Status) ? value.Status : null,
    vendorMessage: messages.length === 0 ? null : messages.join(' '),
  };
}

function journalThrownPayload(error: unknown): Record<string, unknown> {
  if (error instanceof Error) {
    return {
      message: error.message,
      name: error.name,
    };
  }

  return { value: String(error) };
}
